/**
 * Monitoring API client — 탭1 (테넌트 그리드) / 탭2 (실패만) 데이터 조회.
 *
 * 본 스펙: AIFYAGENT/MONITORING_SPEC.md §10
 *
 * Backend endpoints:
 *   GET /api/summary?bsda=YYYYMMDD   — 8 테넌트 그날 요약
 *   GET /api/failures?bsda=YYYYMMDD  — 8 테넌트 통합 실패 행
 */

import { request } from "./base";

export type FixLoopStatus = "pending" | "running" | "success" | "failed" | "escalated";

export type TenantSummary = {
  tenant_id: string;
  tenant_name: string;
  tenant_name_korean: string;
  prompts: {
    total: number;
    success: number;
    partial: number;
    failed: number;
  };
  fix_loop: {
    completed: number;
    running: number;
    failed: number;
  };
};

export type SummaryResponse = {
  bsda: string;
  cached: boolean;
  tenants: TenantSummary[];
};

export type FailureRow = {
  tenant_id: string;
  tenant_name_korean: string;
  prompt_id: string;
  status: FixLoopStatus;
  fail_count: number;
  fix_attempt_count: number;
  last_fix_pattern: string | null;
};

export type FailuresResponse = {
  bsda: string;
  cached: boolean;
  failures: FailureRow[];
};

export async function fetchSummary(bsda: string): Promise<SummaryResponse> {
  return request<SummaryResponse>(`/api/summary?bsda=${encodeURIComponent(bsda)}`);
}

export async function fetchFailures(bsda: string): Promise<FailuresResponse> {
  return request<FailuresResponse>(`/api/failures?bsda=${encodeURIComponent(bsda)}`);
}

/* ────────────────────────── 원본↔HAI 비교 (탭3-c) ────────────────────────── */
// 본 스펙: AIFYAGENT/MONITORING_SPEC.md §6.c §10 §11 Phase 5
// 백엔드: GET /api/tenants/{id}/prompts/{prompt}/compare?bsda=YYYYMMDD

export type CompareSourceSheet = { name: string; rows: number; cols: number };

export type CompareSourceMetrics = {
  sheet_count: number;
  sheets: CompareSourceSheet[];
  total_data_rows: number;
};

export type CompareHaiMetrics = {
  empty: boolean;
  header: string[];
  rows: number;
  unique_date_count: number;
  unique_dates: string[];
  unique_person_count: number;
  unique_persons: string[];
  duplicate_count: number;
  duplicates_sample: { date: string; person: string; count: number }[];
  rate_count: number;
  rate_min: number | null;
  rate_max: number | null;
  rate_sum: number;
  rate_zero_count: number;
  dates_after_cutoff: string[];
  missing_dates_in_month: string[];
};

export type CompareChecks = {
  header_exact: boolean;
  no_duplicates: boolean;
  no_zero_rate: boolean;
  no_dates_after_cutoff: boolean;
  rows_gte_5: boolean;
};

export type ComparePair = {
  channel: string;
  source_id: string;
  ingestion_id: string;
  src_basename: string;
  hai_basename: string;
  extract_date: string | null;
  ingestion_date: string | null;
  source_gcs_path: string;
  hai_gcs_path: string;
  source: CompareSourceMetrics;
  hai: CompareHaiMetrics;
  checks: CompareChecks;
  overall_pass: boolean;
};

export type CompareResponse = {
  bsda: string;
  tenant_id: string;
  prompt_id: string;
  cached: boolean;
  pairs: ComparePair[];
};

export async function fetchCompare(
  tenantId: string,
  promptId: string,
  bsda: string,
): Promise<CompareResponse> {
  const t = encodeURIComponent(tenantId);
  const p = encodeURIComponent(promptId);
  const b = encodeURIComponent(bsda);
  return request<CompareResponse>(
    `/api/tenants/${t}/prompts/${p}/compare?bsda=${b}`,
  );
}

/** 원본/HAI 다운로드 — 기존 /api/tenants/{id}/bucket/download endpoint 재사용. */
export function bucketDownloadUrl(tenantId: string, gcsPath: string): string {
  const t = encodeURIComponent(tenantId);
  const p = encodeURIComponent(gcsPath);
  return `/api/agent-proxy/tenants/${t}/bucket/download?path=${p}`;
}

/* ───────────── 탭3 SessionDetailDialog 풀뷰 (MONITORING_SPEC §6.b / §10 P2) ───────────── */

export interface FixAttemptEntry {
  attempt_number?: number;
  fix_pattern?: string;
  description?: string;
  started_at?: string;
  completed_at?: string;
  status?: string;
  validation_status?: string;
  diff_summary?: string;
  [key: string]: unknown;
}

export interface FixAttemptsRaw {
  prompt_id?: string;
  bsda?: string;
  attempt_count?: number;
  max_retries?: number;
  overall_status?: string;
  started_at?: string;
  completed_at?: string;
  attempts?: FixAttemptEntry[];
  fix_patterns_applied?: string[];
  evidence?: Record<string, unknown>;
  escalation?: { reason?: string; [key: string]: unknown };
  definition_of_done?: Record<string, boolean>;
  [key: string]: unknown;
}

export interface ValidationCheckRaw {
  check_name: string;
  status: string;
  expected?: unknown;
  actual?: unknown;
  detail?: string;
}

export interface ValidationReportRaw {
  prompt_id?: string;
  bsda?: string;
  validated_at?: string;
  validation_method?: string;
  source_file?: string;
  hai_file?: string;
  checks?: ValidationCheckRaw[];
  overall_status?: string;
  overall_reason?: string;
  evidence_hash?: string;
  [key: string]: unknown;
}

export interface PromptDetailGcs {
  bucket: string;
  fix_attempts_path: string;
  validation_report_path: string;
  script_py_path: string;
  hai_prefix: string;
}

export interface PromptDetailResponse {
  tenant_id: string;
  tenant_name_korean: string;
  prompt_id: string;
  bsda: string;
  fix_attempts: FixAttemptsRaw | null;
  validation_report: ValidationReportRaw | null;
  gcs: PromptDetailGcs;
}

export async function fetchPromptDetail(
  tenantId: string,
  promptId: string,
  bsda: string,
): Promise<PromptDetailResponse> {
  const t = encodeURIComponent(tenantId);
  const p = encodeURIComponent(promptId);
  const b = encodeURIComponent(bsda);
  return request<PromptDetailResponse>(
    `/api/tenants/${t}/prompts/${p}/detail?bsda=${b}`,
  );
}

/** GCS 콘솔 링크 — 운영자가 클릭하면 GCS 콘솔에서 파일 상세 또는 prefix 브라우저 열림. */
export function gcsConsoleUrl(bucket: string, objectPath: string): string {
  if (objectPath.endsWith("/")) {
    return `https://console.cloud.google.com/storage/browser/${bucket}/${objectPath}`;
  }
  return `https://console.cloud.google.com/storage/browser/_details/${bucket}/${objectPath}`;
}
